import {useCallback, useEffect, useState} from 'react';
import DataService from '../../shared/services/DataService';
import {Func} from '../types/Func';
import Helpers from '../helpers/Helpers';
import {isUndefined, some} from 'lodash-es';

type FieldValue = string | number | boolean | string[] | number[] | boolean[] | unknown;

type Parameters = { [key: string]: FieldValue };

export interface LoadDataOptions {
  enableLoading: boolean;
  hideErrorMessages: boolean;
  retryOnFailure: boolean;
}

export function useLoadedData<T>(url: string,
                                 parameters: Parameters,
                                 options?: Partial<LoadDataOptions>): [T, boolean, Func, boolean] {

  const [data, setData] = useState<T | null | undefined>(undefined);

  /**
   * Whether the last attempt failed.
   *
   * Tracked separately from `data` because a failed load cannot be seen in `data` alone: the hook stores
   * `Helpers.ifNil(result, null)`, and ifNil skips a nil replacement, so a failed load leaves `data`
   * undefined - indistinguishable from a load still in flight. That is why a caller relying on `isLoading`
   * alone spins forever after a failure. Changing what `data` becomes would risk the many callers that guard
   * only on `isLoading` and then dereference `data`, so the fact is reported alongside instead.
   */
  const [hasFailed, setHasFailed] = useState<boolean>(false);

  const optionsWithDefaults = Object.assign(
    {
      hideErrorMessages: false,
      retryOnFailure: false,
      enableLoading: true
    },
    options
  );

  useEffect(
    () => {
      setData(undefined);
      setHasFailed(false);
    },
    [...Object.values(parameters)]
  );

  useEffect(
    (): void | (() => void | undefined) => {
      if (!isUndefined(data)) {
        return undefined;
      }

      if (some(Object.values(parameters), p => isUndefined(p))) {
        return undefined;
      }

      if (optionsWithDefaults.enableLoading === false) {
        return undefined;
      }

      const abortController = new AbortController();

      const fetchFunction = optionsWithDefaults.retryOnFailure
        ? () => DataService.tryLoadWithRetries<T>(
          url,
          parameters,
          optionsWithDefaults.hideErrorMessages,
          abortController.signal
        )
        : () => DataService.tryLoad<T>(
          url,
          parameters,
          optionsWithDefaults.hideErrorMessages,
          abortController.signal
        );

      // POST to the specified URL passing the required parameters to load the data
      fetchFunction().then(result => {
        setHasFailed(isUndefined(result));
        setData(Helpers.ifNil(result, null));
      }).catch(() => {
        setHasFailed(true);
        setData(null);
      });

      // Abort the running load if the component unmounts, or if the URL or parameters change making the loaded
      // value out of date
      return () => {
        abortController.abort();
      };
    },
    [url, data, optionsWithDefaults.enableLoading, ...Object.values(parameters)]
  );

  const reloadData = useCallback(
    () => {
      setData(undefined);
      setHasFailed(false);
    },
    [setData]
  );

  return [
    // The loaded data; otherwise undefined if not loaded yet
    data,
    // true if the hook is currently loading data; otherwise false
    optionsWithDefaults.enableLoading === true && isUndefined(data),
    // A callback to trigger a reload of the data
    reloadData,
    // true if the last attempt failed. Note this can be true while isLoading is also true - see hasFailed.
    hasFailed
  ];
}
